/* eslint-disable no-console */

import Cookies from 'js-cookie'
import { OverlayConstants } from '../components/OverlayContext'

interface MktoForm {
  onSubmit(callback: (form: MktoForm) => void): void
  onSuccess(
    callback: (form: MktoForm, values: Record<string, any>) => void
  ): void
  addHiddenFields(values: Record<string, any>): void
  vals(): Record<string, any>
  submit(): void
  // eslint-disable-next-line no-undef
  getFormElem(): JQuery<HTMLFormElement>
  // eslint-disable-next-line no-undef
  getField(fieldName: string): JQuery<HTMLElement>
  getId(): number
  show(): void
  hide(): void
  destroy(): void
  focus(): void
  clearValues(): void
  setValues(values: Record<string, any>): void
  getValues(): Record<string, any>
}

interface MktoForms2 {
  loadForm(
    baseUrl: string,
    munchkinId: string,
    formId: number | string,
    callback?: (form: MktoForm) => void
  ): void
  getForm(formId: number): MktoForm | null
  whenReady(callback: (form: MktoForm) => void): void
  all(callback: (forms: MktoForm[]) => void): void
  // eslint-disable-next-line no-undef
  getFormElem(formId: number): JQuery<HTMLFormElement> | null
  // eslint-disable-next-line no-undef
  getFieldContext(fieldName: string): JQuery<HTMLElement>
}

declare global {
  interface Window {
    MktoForms2: MktoForms2
  }
}

interface FormRequest {
  formId: string
  isOverlay: boolean
}

const { SF_MARKETO_MUNCHKIN_ID } = process.env

/**
 * A store for most recent request metadata for each form id.
 */
const formRequests: { [key: string]: FormRequest } = {}

/**
 * Tracks whether shared Marketo listeners are registered.
 */
let isInitialized = false

function setPdfRegistrationUrl(form: MktoForm) {
  form.setValues({
    registrationPDF: window.location.host + window.location.pathname
  })
}

function autoFillFromCookie(form: MktoForm) {
  const marketoCookie = Cookies.get('marketoHold')
  if (marketoCookie) {
    const autofillValues = {
      ...JSON.parse(marketoCookie),
      formid: String(form.getId()),
      munchkinId: SF_MARKETO_MUNCHKIN_ID
    }
    form.setValues(autofillValues)
  }
}

function storeUserInputs(form: MktoForm) {
  const marketoHold = JSON.stringify(form.vals())
  const expirationDate = new Date()
  expirationDate.setDate(expirationDate.getDate() + 365)

  Cookies.set('marketoHold', marketoHold, {
    expires: expirationDate
  })
}

export function setUniqueIds(form: MktoForm) {
  const formEl = form.getFormElem()[0]
  const formId = form.getId()
  const changedElementIds: Record<string, string> = {}
  // Update all input, textarea, select, button and fieldset elements
  formEl
    .querySelectorAll('input, textarea, select, button, fieldset')
    // @ts-ignore
    .forEach((field: HTMLInputElement) => {
      /* eslint-disable no-param-reassign */
      const fieldName = field.name
      if (fieldName) {
        const newFieldId = `${fieldName}_${formId}`
        field.id = newFieldId
        changedElementIds[fieldName] = newFieldId
        // Update associated label
        formEl
          .querySelectorAll(`label[for="${fieldName}"]`)
          .forEach((label) => {
            const uniqueLabelId = `Lbl${fieldName}_${formId}`
            label.setAttribute('for', newFieldId)
            label.classList.add(`for-${fieldName}`)
            // Update label ID if it follows "Lbl<FieldName>" pattern
            if (label.id && label.id.startsWith('Lbl')) {
              const oldLabelId = label.id
              label.id = uniqueLabelId
              changedElementIds[oldLabelId] = uniqueLabelId
            }
          })
        // Update associated instruction span
        formEl
          .querySelectorAll(`span[id="Instruct${fieldName}"]`)
          .forEach((span) => {
            const uniqueSpanId = `Instruct${fieldName}_${formId}`
            if (span.id && span.id.startsWith('Instruct')) {
              const oldSpanId = span.id
              span.id = uniqueSpanId
              changedElementIds[oldSpanId] = uniqueSpanId
            }
          })
      }
    })

  formEl.querySelectorAll('[aria-labelledby]').forEach((elm) => {
    const oldIds = elm.getAttribute('aria-labelledby')?.trim().split(/ +/) || []
    const newIds = oldIds.map((id) => changedElementIds[id] || id)
    elm.setAttribute('aria-labelledby', newIds.join(' '))
  })
}

/**
 * Waits for HTML element to be in DOM before executing `callback`.
 * @param elementId ID of the element to wait for.
 * @param callback Function to execute once html element is detected in DOM.
 */
function waitForElementId(elementId: string, callback: () => void) {
  console.info(`waiting for html element [${elementId}]`)

  const isReady = () => !!document.getElementById(elementId)
  const intervalMillis = 50
  const id = window.setInterval(() => {
    if (isReady()) {
      window.clearInterval(id)
      console.info(`html element [${elementId}] found, proceeding`)
      callback()
    }
  }, intervalMillis)
}

function isMarketoForms2Enabled() {
  return !!(typeof window !== 'undefined' && window.MktoForms2)
}

/**
 * Parse Marketo form ids from static HTML text/string.
 * @returns Array of marketo form ids, maybe empty.
 */
export function parseMarketoFormIds(htmlText: string) {
  return [...htmlText.matchAll(/mktoForm_(\d+)/gi)].map((m) => m[1])
}

/**
 * Registers permanent MktoForms2 listeners, for when
 * CustomContent scripts call MktoForms2.loadForm directly.
 */
export function prepareMarketoLoader() {
  if (isInitialized || !isMarketoForms2Enabled()) {
    return
  }
  window.MktoForms2.whenReady((form: MktoForm) => {
    const thisFormId = String(form.getId())
    const request = formRequests[thisFormId] || {}

    setUniqueIds(form)

    if (request.isOverlay) {
      setPdfRegistrationUrl(form)
      autoFillFromCookie(form)

      form.onSuccess(() => {
        form.getFormElem().hide()
        storeUserInputs(form)
        return false
      })
    }
  })
  isInitialized = true
  console.info('marketo loader initialized')
}

export default (
  formId: string = '',
  toggleOverlay?: (overlay: OverlayConstants) => void,
  setMarketoFormId?: (id: string) => void
) => {
  if (!isMarketoForms2Enabled()) {
    console.info('MktoForms2 is undefined, ignoring request to load', formId)
    return
  }

  prepareMarketoLoader()

  const isOverlay = !!(toggleOverlay && setMarketoFormId)
  formRequests[formId] = {
    formId,
    isOverlay
  }

  if (isOverlay) {
    setMarketoFormId(formId)
    toggleOverlay(OverlayConstants.MARKETO)
  }
  waitForElementId(`mktoForm_${formId}`, () => {
    console.info('loading form', formRequests[formId], '...')
    window.MktoForms2.loadForm(
      '//your.sustainablefitch.com',
      SF_MARKETO_MUNCHKIN_ID as string,
      formId
    )
  })
}
